import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router-dom';
import { queryClient } from '@/lib/queryClient';
import { router } from '@/router';
import { ErrorBoundary } from '@/components/ui';
import '@/index.css';

// ── Apply persisted theme before first paint (avoids flash) ───────────────
(function () {
  try {
    const stored = localStorage.getItem('aidr-theme');
    if (stored) {
      const { state } = JSON.parse(stored) as { state?: { theme?: string } };
      if (state?.theme === 'light') {
        document.documentElement.setAttribute('data-theme', 'light');
      }
    }
  } catch {
    // ignore parse errors
  }
})();

// ── Toast queue (lightweight — no external dep) ────────────────────────────

let _showToast: ((msg: string) => void) | null = null;

function ToastProvider() {
  useEffect(() => {
    const container = document.createElement('div');
    container.id = 'toast-root';
    container.style.cssText =
      'position:fixed;bottom:1rem;right:1rem;z-index:9999;display:flex;flex-direction:column;gap:0.5rem;pointer-events:none';
    document.body.appendChild(container);

    _showToast = (msg: string) => {
      const el = document.createElement('div');
      el.textContent = msg;
      el.style.cssText =
        'background:var(--color-surface-2,#1e1e2e);color:var(--color-text-primary,#e2e2e9);border:1px solid var(--color-state-danger,#f38ba8);border-radius:0.375rem;padding:0.5rem 0.875rem;font-size:0.75rem;opacity:0;transition:opacity 0.15s;pointer-events:auto;max-width:20rem;word-break:break-word';
      container.appendChild(el);
      // Fade in
      requestAnimationFrame(() => { el.style.opacity = '1'; });
      // Fade out and remove after 4s
      setTimeout(() => {
        el.style.opacity = '0';
        setTimeout(() => container.removeChild(el), 160);
      }, 4000);
    };

    return () => {
      _showToast = null;
      document.body.removeChild(container);
    };
  }, []);

  return null;
}

// Wire mutation errors into the toast queue at QueryClient level.
// A mutation can opt out (e.g. when it already renders its own inline error)
// via `meta: { suppressErrorToast: true }`.
queryClient.getMutationCache().config.onError = (error, _variables, _context, mutation) => {
  if (mutation.options.meta?.suppressErrorToast) return;
  const msg =
    error instanceof Error ? error.message : 'An unexpected error occurred.';
  _showToast?.(`Mutation error: ${msg}`);
};

// ── Root ───────────────────────────────────────────────────────────────────

const root = document.getElementById('root');
if (!root) throw new Error('Root element #root not found');

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider />
        <RouterProvider router={router} />
        {/* {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />} */}
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
